<!-- components/DataPeek.vue (Vue 3 + <script setup> + CSS Modules) -->
<template>
  <div
    v-show="visible"
    ref="panel"
    :class="[$style.overlay, $style[corner]]"
    :style="panelStyle"
    data-json-overlay
    role="dialog"
    aria-label="Data overlay"
    data-peek-v="3.2.0"
  >
    <header :class="$style.header">
      <span :class="$style.title">{{ title }}</span>
      <div :class="$style.actions">
        <button :class="$style.btn" @click="copy">Copy</button>
        <button :class="$style.btn" @click="close">Close</button>
      </div>
    </header>

    <div :class="$style.body" @dblclick="toggleWrap">
      <pre :class="[$style.pre, wrap ? $style.wrap : null]">
<code v-html="highlighted"/>
      </pre>
    </div>

    <!-- handle sits on the corner opposite the anchor -->
    <div
      :class="[$style.resize, $style[handleCornerKey], $style[handleCursorKey]]"
      title="Drag to resize"
      @mousedown="startResize"
    />
  </div>

  <button
    v-if="!visible"
    :class="{
      [$style.fab]: true,
      [$style[corner]]: true,
    }"
    @click="openPanel"
  >
    {{ label }}
  </button>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'

defineOptions({ name: 'DataPeek' })

type Corner = 'br' | 'tr' | 'bl' | 'tl'
type HandlePosition = 'handleTL' | 'handleBL' | 'handleTR' | 'handleBR'
type CursorType = 'cursorNWSE' | 'cursorNESW'
type Position = 'fixed' | 'absolute'

const {
  data = null,
  title = 'Debug Data',
  label = 'Data',
  open = false,
  corner = 'br',
  maxWidth = 'min(960px, 90vw)',
  maxHeight = '90cqh',
  minWidth = 320,
  minHeight = 200,
  position = 'absolute',
} = defineProps<{
  data?: unknown
  title?: string
  label?: string
  open?: boolean
  corner?: Corner
  maxWidth?: string
  maxHeight?: string
  minWidth?: number
  minHeight?: number
  position?: Position
}>()

const panel = ref<HTMLElement | null>(null)
const visible = ref<boolean>(open)
const wrap = ref<boolean>(true)
const size = ref<{ w: number | null; h: number | null }>({ w: null, h: null })
const emit = defineEmits<{ (e: 'update:open', v: boolean): void }>()

let resizing = false
let startX = 0
let startY = 0
let startW = 0
let startH = 0

watch(
  () => open,
  (v) => {
    visible.value = v
  },
  { immediate: true },
)

const panelStyle = computed(() => {
  const s: Record<string, string> = {
    maxWidth,
    maxHeight,
  }
  if (size.value.w) s.width = `${size.value.w}px`
  if (size.value.h) s.height = `${size.value.h}px`
  return s
})

/** Opposite of anchor corner — return the *key* and let template map to $style[key] */
const handleCornerKey = computed<
  'handleTL' | 'handleBL' | 'handleTR' | 'handleBR'
>((): HandlePosition => {
  return { br: 'handleTL', tr: 'handleBL', bl: 'handleTR', tl: 'handleBR' }[
    corner
  ] as HandlePosition
})

/** TL<->BR uses nwse; TR<->BL uses nesw — again return the *key* for $style[...] */
const handleCursorKey = computed<'cursorNWSE' | 'cursorNESW'>(
  (): CursorType => {
    return {
      br: 'cursorNWSE',
      tl: 'cursorNWSE',
      tr: 'cursorNESW',
      bl: 'cursorNESW',
    }[corner] as CursorType
  },
)

const pretty = computed(() => safeStringify(data ?? {}))

const highlighted = computed(() => {
  const esc = (s: string) =>
    s.replace(
      /[&<>"']/g,
      (m) =>
        ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        })[m] as string,
    )
  const json = esc(pretty.value)
  return json
    .replace(/(&quot;.*?&quot;)(?=:)/g, '<span class="k">$1</span>')
    .replace(/: (true|false|null)([,}\n\r])/g, ': <span class="b">$1</span>$2')
    .replace(
      /: (-?\\d+(?:\\.\\d+)?)([,}\\n\\r])/g,
      ': <span class="n">$1</span>$2',
    )
    .replace(/: (&quot;.*?&quot;)([,}\n\r])/g, ': <span class="s">$1</span>$2')
})

function safeStringify(v: unknown): string {
  const seen = typeof WeakSet !== 'undefined' ? new WeakSet() : null
  try {
    return JSON.stringify(
      v,
      function (_k, val) {
        if (seen && val && typeof val === 'object') {
          if (seen.has(val)) return '[Circular]'
          seen.add(val)
        }
        return val
      },
      2,
    )
  } catch {
    return String(v)
  }
}

function openPanel() {
  visible.value = true
  emit('update:open', true)
}

function close() {
  visible.value = false
  emit('update:open', false)
}

function toggleWrap() {
  wrap.value = !wrap.value
}

async function copy() {
  const text = pretty.value
  try {
    if ('clipboard' in navigator && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
    } else {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
  } catch {
    // noop
  }
}

function startResize(e: MouseEvent) {
  if (!panel.value) return
  resizing = true
  startX = e.clientX
  startY = e.clientY
  startW = panel.value.offsetWidth
  startH = panel.value.offsetHeight
  window.addEventListener('mousemove', onResize)
  window.addEventListener('mouseup', endResize)
  document.body.style.userSelect = 'none'
}

function onResize(e: MouseEvent) {
  if (!resizing) return
  const dx = e.clientX - startX
  const dy = e.clientY - startY
  let nextW = startW
  let nextH = startH
  switch (corner) {
    case 'tl':
      nextW = startW + dx
      nextH = startH + dy
      break
    case 'tr':
      nextW = startW - dx
      nextH = startH + dy
      break
    case 'bl':
      nextW = startW + dx
      nextH = startH - dy
      break
    case 'br':
    default:
      nextW = startW - dx
      nextH = startH - dy
      break
  }
  size.value.w = Math.max(minWidth, nextW)
  size.value.h = Math.max(minHeight, nextH)
}

function endResize() {
  if (!resizing) return
  resizing = false
  window.removeEventListener('mousemove', onResize)
  window.removeEventListener('mouseup', endResize)
  document.body.style.userSelect = ''
}

onBeforeUnmount(() => {
  endResize()
})
</script>

<style lang="scss" module>
.overlay {
  position: var(--dp-position, v-bind(position));
  z-index: 2147483000;
  width: var(--dp-init-w, 540px);
  max-height: var(--dp-max-h, 90cqh);
  min-height: var(--dp-min-h, 200px);
  background: rgba(16, 18, 22, 0.92);
  backdrop-filter: blur(10px);
  color: #e7eaf0;
  font-family: Helvetica, Arial, sans-serif;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.35);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  user-select: text;
}
.br {
  right: 18px;
  bottom: 18px;
}
.tr {
  right: 18px;
  top: 18px;
}
.bl {
  left: 18px;
  bottom: 18px;
}
.tl {
  left: 18px;
  top: 18px;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 30px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.06),
    rgba(255, 255, 255, 0)
  );
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.title {
  font-weight: 600;
  font-size: 14px;
  opacity: 0.9;
}
.actions {
  display: flex;
  gap: 8px;
}
.btn {
  all: unset;
  cursor: pointer;
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.06);
  transition:
    transform 0.12s ease,
    background 0.12s ease;
}
.btn:hover {
  background: rgba(255, 255, 255, 0.12);
  transform: translateY(-1px);
}

.body {
  text-align: left;
  padding: 8px 12px 12px;
  overflow: auto;
}
.pre {
  margin: 0;
  font-family:
    ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
    monospace;
  font-size: 12.5px;
  line-height: 1.45;
  tab-size: 2;
  white-space: pre;
}
.wrap {
  white-space: pre-wrap;
  word-break: break-word;
}

/* FAB */
.fab {
  position: var(--dp-position, v-bind(position));
  z-index: 2147483000;
  bottom: 18px;
  right: 18px;
  border: none;
  border-radius: 999px;
  padding: 10px 14px;
  font-weight: 600;
  background: #111827;
  color: #e5e7eb;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  cursor: pointer;

  &.tl {
    top: 18px;
    left: 18px;
    right: auto;
    bottom: auto;
  }

  &.tr {
    top: 18px;
    right: 18px;
    left: auto;
    bottom: auto;
  }

  &.bl {
    bottom: 18px;
    left: 18px;
    right: auto;
    top: auto;
  }

  &.br {
    bottom: 18px;
    right: 18px;
    left: auto;
    top: auto;
  }
}

.fab:hover {
  filter: brightness(1.1);
}

/* handle positions: opposite the anchor */
.resize {
  position: absolute;
  width: 12px;
  height: 12px;
  opacity: 0.85;
  border-radius: 2px;
  /* no borders here; set them per-corner */
}

/* anchor = tl → handleBR */
.handleBR {
  right: 6px;
  bottom: 6px;
  border-right: 2px solid rgba(255, 255, 255, 0.35);
  border-bottom: 2px solid rgba(255, 255, 255, 0.35);
}

/* anchor = bl → handleTR */
.handleTR {
  right: 6px;
  top: 6px;
  border-right: 2px solid rgba(255, 255, 255, 0.35);
  border-top: 2px solid rgba(255, 255, 255, 0.35);
}

/* anchor = tr → handleBL */
.handleBL {
  left: 6px;
  bottom: 6px;
  border-left: 2px solid rgba(255, 255, 255, 0.35);
  border-bottom: 2px solid rgba(255, 255, 255, 0.35);
}

/* anchor = br → handleTL */
.handleTL {
  left: 6px;
  top: 6px;
  border-left: 2px solid rgba(255, 255, 255, 0.35);
  border-top: 2px solid rgba(255, 255, 255, 0.35);
}

/* cursors */
.cursorNWSE {
  cursor: nwse-resize;
}
.cursorNESW {
  cursor: nesw-resize;
}

/* IMPORTANT: token colors need to stay global (v-html) */
:global(.k) {
  color: #8ab4f8;
}
:global(.s) {
  color: #a1e8af;
}
:global(.n) {
  color: #ffd480;
}
:global(.b) {
  color: #ff9aa2;
}
</style>

<!-- Optional: global max-height var -->
<style>
[data-json-overlay] {
  --dp-max-h: 90vh;
}
</style>
